import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';

const CasteManagement = ({ isModal = false, onClose, onCasteCreated }) => {
  const [castes, setCastes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [castePage, setCastePage] = useState(0);
  const [casteRowsPerPage, setCasteRowsPerPage] = useState(50);
  const [casteTotalDocs, setCasteTotalDocs] = useState(0);

  // Create/Edit Caste State
  const [casteForm, setCasteForm] = useState({
    _id: '',
    name: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Delete Caste State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [casteToDelete, setCasteToDelete] = useState(null);

  const fetchCastesPage = async (page = 0, rowsPerPage = 50) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/castes`, {
        params: { page: page + 1, limit: rowsPerPage },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = response.data;
      setCastes(res.data || []);
      setCasteTotalDocs(res.totalDocs ?? (res.data || []).length);
      setCastePage(page);
      setCasteRowsPerPage(rowsPerPage);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch castes');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCastesPage(0, casteRowsPerPage);
  }, []);

  const handleCastePageChange = (event, newPage) => {
    fetchCastesPage(newPage, casteRowsPerPage);
  };

  const handleCasteRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setCasteRowsPerPage(newRows);
    fetchCastesPage(0, newRows);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert text inputs to uppercase
    const upperValue = value ? value.toUpperCase() : '';
    setCasteForm(prev => ({
      ...prev,
      [name]: upperValue
    }));
  };

  const handleCreateCaste = async (e) => {
    e.preventDefault();
    
    // Validate input
    if (!casteForm.name.trim()) {
      setCreateError('Caste name cannot be empty');
      return;
    }

    setCreateLoading(true);
    setCreateError(null);

    try {
      const token = localStorage.getItem('token');
      let response;

      if (isEditing) {
        // Update existing caste
        response = await axios.patch(`${baseURL}/castes/${casteForm._id}`, casteForm, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

      } else {
        // Create new caste
        response = await axios.post(`${baseURL}/castes`, casteForm, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      // Reset form and refresh current page
      setCasteForm({ _id: '', name: '' });
      setIsEditing(false);
      setCreateSuccess(true);
      setCreateLoading(false);
      fetchCastesPage(castePage, casteRowsPerPage);
      
      // If in modal mode, call the callback
      if (isModal && onCasteCreated) {
        onCasteCreated(response.data.data);
      }
    } catch (err) {
      // Handle specific error messages from backend
      const errorMessage = err.response?.data?.message || 
        'Failed to create/update caste';
      
      setCreateError(errorMessage);
      setCreateLoading(false);
    }
  };

  const handleEditCaste = (caste) => {
    setCasteForm({
      _id: caste._id,
      name: caste.name
    });
    setIsEditing(true);
  };

  const handleDeleteCaste = async () => {
    if (!casteToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/castes/${casteToDelete._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDeleteDialogOpen(false);
      setCasteToDelete(null);
      setCreateSuccess(true);
      fetchCastesPage(castePage, casteRowsPerPage);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to delete caste');
    }
  };

  const handleCloseSnackbar = () => {
    setCreateSuccess(false);
    setCreateError(null);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  // If in modal mode, render only the form
  if (isModal) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {isEditing ? 'Edit Caste' : 'Create New Caste'}
        </Typography>
        <Box component="form" onSubmit={handleCreateCaste} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label="Caste Name"
            name="name"
            value={casteForm.name}
            onChange={handleInputChange}
          />
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={createLoading}
            >
              {isEditing ? 'Update' : 'Create'}
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Cancel
            </Button>
          </Box>
        </Box>
        
        {/* Success/Error Snackbar */}
        <Snackbar
          open={createSuccess || !!createError}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={createError ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {createError || 'Operation successful!'}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <MasterPageShell title="Caste" subtitle="Master · Parties">
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>
                {isEditing ? 'Edit caste' : 'Create caste'}
              </Typography>
              <Box component="form" onSubmit={handleCreateCaste} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Caste name"
                  name="name"
                  value={casteForm.name}
                  onChange={handleInputChange}
                  sx={masterFieldSx}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={masterPrimaryBtnSx}
                  disabled={createLoading}
                >
                  {isEditing ? 'Update caste' : 'Create caste'}
                </Button>
                {isEditing && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setIsEditing(false);
                      setCasteForm({ _id: '', name: '' });
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 650 }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={masterListPaperSx}>
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography sx={{ ...masterSectionTitleSx, mb: 0 }}>Caste list</Typography>
              </Box>
              <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={masterTableHeadCellSx}>Caste name</TableCell>
                    <TableCell align="right" sx={masterTableHeadCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {castes.map((caste) => (
                    <TableRow key={caste._id} hover>
                      <TableCell sx={{ fontWeight: 650 }}>{caste.name}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="primary"
                          size="small"
                          onClick={() => handleEditCaste(caste)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          color="error"
                          size="small"
                          onClick={() => {
                            setCasteToDelete(caste);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={casteTotalDocs}
                page={castePage}
                onPageChange={handleCastePageChange}
                rowsPerPage={casteRowsPerPage}
                onRowsPerPageChange={handleCasteRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
                labelRowsPerPage="Rows per page:"
              />
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this caste?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              color="error" 
              onClick={handleDeleteCaste}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbar */}
        <Snackbar
          open={createSuccess || !!createError}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={createError ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {createError || 'Operation successful!'}
          </Alert>
        </Snackbar>
      </>
    </MasterPageShell>
  );
};

export default CasteManagement;